import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useToast } from "../../../hooks/use-toast";
import { handleHttpError } from "../../../utils/utils";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { Button } from "../../../components/ui/button";
import {
  useGetAllVendorCampaignsMethodsHttp,
  type IVendorCampaign,
} from "../../../hooks/http/admin/vendors/campaigns/get-all-vendor-campaigns.admin.http";
import { DataTableComponents } from "../../../components/tables/table.components";
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import Spinner from "../../../components/spinner.components";

export default function VendorCampaignsAdminPartial() {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [paginationData, setPaginationData] = useState<IPagination>();

  const [rowSelection, setRowSelection] = useState({});
  const { vendorId } = useParams();
  const { toast } = useToast();

  const { fetching: gettingVendorCampaigns, execute: getVendorCampaignsHttp } =
    useGetAllVendorCampaignsMethodsHttp();

  const [vendorCampaigns, setVendorCampaigns] = useState<IVendorCampaign[]>([]);
  const [pageNo, setPageNo] = useState(1);

  async function getVendorCampaigns(page: number) {
    if (!vendorId) return;

    // cancelGetVendorCampaigns();

    const response = await getVendorCampaignsHttp({
      body: null,
      pathParams: { vendorId },
      queryParams: { page },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    setVendorCampaigns((prevData) => {
      const newCampaigns = response.data.vendorCampaigns.filter(
        (vendorCampaign) => {
          return !prevData.find((vc) => vc.id === vendorCampaign.id);
        }
      );

      return [...prevData, ...newCampaigns];
    });

    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (vendorId) {
      getVendorCampaigns(pageNo);
    }
  }, [pageNo, vendorId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setVendorCampaigns([]);
    setPageNo(1);
  }, [vendorId]);

  const columns: ColumnDef<IVendorCampaign>[] = [
    {
      header: "Campaign name",
      accessorKey: "campaign",
      cell: ({ row }) => {
        const { campaign } = row.original;
        return (
          <div className="font-semibold tracking-wider">{campaign.name}</div>
        );
      },
    },
    {
      header: "",
      accessorKey: "id",
      cell: ({ row }) => {
        const { campaign } = row.original;

        return (
          <div className="flex justify-end items-center gap-3">
            <Link to={`${campaign.id}/manage-posts`}>
              <Button className="bg-tertiarybuttonbg hover:bg-white/5 px-5 py-2.5 border border-border font-semibold text-white tracking-wider">
                Manage posts →
              </Button>
            </Link>
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data: vendorCampaigns,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  if (vendorCampaigns.length <= 0 && gettingVendorCampaigns) {
    return (
      <div className="flex justify-center items-center pt-20 w-full">
        <Spinner width={20} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 px-2 pt-5 w-full">
      <DataTableComponents
        table={table as ReturnType<typeof useReactTable>}
        columns={columns}
        loading={gettingVendorCampaigns}
        paginationData={paginationData}
        setPageNo={() => {
          setPageNo((prev) => {
            const nextPage = prev + 1;
            getVendorCampaigns(nextPage);
            return nextPage;
          });
        }}
        tableType="vendorCampaigns"
      />
    </div>
  );
}
